'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function FounderSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el, i) => {
              setTimeout(() => el.classList.remove('reveal-hidden'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Portrait — 5 cols */}
          <div className="reveal reveal-hidden lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden card-rounded bg-secondary">
              <AppImage
                src="/assets/images/kym-products/about-founder.png"
                alt="Kym, founder of SweetSkyn — natural skincare rooted in healing and self-care"
                fill
                sizes="(max-width: 1024px) 100vw, 42vw"
                className="object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-4 md:-right-8 glass-card p-6 card-rounded shadow-xl max-w-[220px]">
              <p className="font-display text-lg font-light text-foreground leading-tight">Certified Skincare Coach</p>
              <p className="text-[11px] text-muted-foreground mt-1">Guiding every customer in understanding their skin</p>
            </div>
          </div>

          {/* Story — 7 cols */}
          <div className="lg:col-span-7 min-w-0">
            <div className="reveal reveal-hidden">
              <span className="section-label block mb-4">Meet the Founder</span>
              <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-foreground leading-tight mb-8">
                Hi, I&rsquo;m <em className="font-light text-accent">Kym.</em>
              </h2>
            </div>

            <div className="reveal reveal-hidden space-y-5 text-base text-muted-foreground leading-relaxed">
              <p>
                I&rsquo;m the passionate founder and visionary behind <span className="text-foreground font-medium">SweetSkyn</span> — a natural skincare brand rooted in healing, self-care, and confidence. My journey into skincare began after facing my own health challenges, including endometriosis. I became more intentional about what I put on my body and how I cared for myself from the inside out.
              </p>
              <p>
                What started as a personal mission to live healthier and more naturally soon evolved into something bigger. I realized that many people struggle with dry, sensitive, and neglected skin — often using products filled with harsh chemicals and ingredients that affect our hormones.
              </p>
              <p>
                That&rsquo;s when I began creating rich, nourishing body butters made with natural ingredients to restore, hydrate, and protect the skin.
              </p>
              <p>
                As I embarked on this journey so did my passion for natural skincare. I became a certified skincare coach — allowing me to go beyond just selling products. I now guide my customers in understanding their skin, building effective routines, and feeling confident in their natural beauty.
              </p>
              <p className="text-foreground">
                For me, SweetSkyn is more than a business. It&rsquo;s a movement of self-love, healing, and empowerment. Every jar I create reflects my journey and my mission to help others <em className="text-accent">glow, naturally.</em>
              </p>
            </div>

            <div className="reveal reveal-hidden mt-10 pt-6 border-t border-border">
              <p className="font-display text-xl text-foreground">Kym</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Founder · SweetSkyn by Kym</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
